import { Link } from 'react-router-dom'
import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import { normalizeQuantityByUnit } from '@crm/shared'
import { SelectAllHeader } from './SelectAllHeader'
import { MatrixSelect } from './MatrixSelect'
import { TextareaEditor } from '../../table/TextareaEditor'
import type { ItemColDefsParams, ItemRow } from './itemColDefs.types'

/**
 * Левая часть сетки позиций: выбор строк, нумерация, матрица, переход в позицию
 * и карточные поля (артикул, бренд, наименование, заметка, количество).
 * Вынесено из itemColDefs.tsx ради правила «файлы ≤500 строк», поведение прежнее.
 */
export function buildBaseItemColDefs({
  isAdmin,
  userRole,
  userId,
  isProcurement,
  requestId,
  queryClient,
}: ItemColDefsParams): ColDef<ItemRow>[] {
  return [
    // Колонка чекбокса выбора — ADMIN/MANAGER/ROP (массовые операции продажи)
    // и PROCUREMENT/ROS (массовое скачивание запроса + статус «Поставщик не ответил»).
    ...(isAdmin ||
    userRole === 'MANAGER' ||
    userRole === 'ROP' ||
    userRole === 'ASSISTANT' ||
    userRole === 'PROCUREMENT' ||
    userRole === 'ROS'
      ? ([
          {
            colId: 'select',
            headerName: '',
            width: 36,
            minWidth: 36,
            maxWidth: 36,
            pinned: 'left',
            lockPinned: true,
            lockPosition: 'left',
            resizable: false,
            sortable: false,
            suppressMovable: true,
            filter: false,
            cellClass: 'ag-cell-no-padding',
            headerClass: 'ag-cell-no-padding',
            headerComponent: SelectAllHeader,
            cellRenderer: (p: ICellRendererParams<ItemRow>) => (
              <div className="flex items-center justify-center w-full h-full">
                <input
                  type="checkbox"
                  checked={p.node.isSelected() ?? false}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => p.node.setSelected(e.target.checked, false)}
                  className="cursor-pointer"
                />
              </div>
            ),
          },
        ] as ColDef<ItemRow>[])
      : []),

    // Нумерация позиций — после чекбокса (или первой, если чекбокса нет).
    // valueGetter по rowIndex даёт номер так, как сейчас отсортирована таблица:
    // менеджер видит «1, 2, 3…» в порядке position (DnD на новой заявке).
    {
      colId: 'rowNumber',
      headerName: '№',
      width: 56,
      minWidth: 40,
      maxWidth: 120,
      pinned: 'left',
      lockPinned: true,
      lockPosition: 'left',
      resizable: true,
      sortable: false,
      suppressMovable: true,
      filter: false,
      cellClass: 'text-xs text-gray-500 text-center',
      headerClass: 'text-center',
      valueGetter: (p: { node: { rowIndex: number | null } | null }) => (p.node?.rowIndex ?? 0) + 1,
    },

    // Колонка «Матрица» — скрыта для PROCUREMENT
    ...(!isProcurement
      ? ([
          {
            colId: 'matrix',
            field: 'matrix',
            headerName: 'Матрица',
            width: 120,
            sortable: false,
            cellRenderer: (p: ICellRendererParams<ItemRow>) => (
              <MatrixSelect
                itemId={p.data!.id as string}
                currentMatrix={(p.data!.matrix as string) ?? ''}
                requestId={requestId}
                queryClient={queryClient}
              />
            ),
          },
        ] as ColDef<ItemRow>[])
      : []),

    {
      colId: 'open',
      headerName: '',
      width: 32,
      minWidth: 32,
      maxWidth: 32,
      resizable: false,
      sortable: false,
      suppressMovable: true,
      lockPinned: true,
      lockVisible: true,
      suppressNavigable: true,
      filter: false,
      cellClass: 'ag-cell-no-padding',
      headerClass: 'ag-cell-no-padding',
      cellRenderer: (p: ICellRendererParams<ItemRow>) => (
        <div className="flex items-center justify-center w-full h-full">
          <Link
            to={`/request-items/${p.data!.id as string}`}
            title="Открыть позицию"
            className="text-brand-500 hover:text-brand-700 text-sm leading-none"
          >
            ↗
          </Link>
        </div>
      ),
    },

    {
      colId: 'partNumber',
      field: 'partNumber',
      headerName: 'Арт.',
      editable: isAdmin || userRole === 'MANAGER' || userRole === 'ROP' || userRole === 'ASSISTANT',
      cellClassRules: {
        'crm-cell-invalid': (p) => (p.data?.partNumber as string) === '???',
      },
      cellRenderer: (p: ICellRendererParams<ItemRow>) => {
        const results =
          (p.data!.results as { createdBy?: string; creator?: { id: string } | null }[]) ?? []
        const hasAnyQuote = results.length > 0
        const myQuote = !!userId && results.some((r) => (r.creator?.id ?? r.createdBy) === userId)

        const role = userRole
        let show = false
        let mine = false
        if (role === 'PROCUREMENT') {
          show = myQuote
          mine = myQuote
        } else if (role === 'ROS') {
          show = hasAnyQuote
          mine = myQuote
        } else {
          show = hasAnyQuote
          mine = myQuote
        }

        return (
          <span className="inline-flex items-center gap-1 font-mono text-xs font-medium text-gray-800">
            {p.data!.partNumber as string}
            {show && (
              <span
                className={`text-xs ${mine ? 'text-green-600 font-semibold' : 'text-green-500'}`}
                title={mine ? 'Вы дали квоту' : 'Есть квота'}
              >
                ✓
              </span>
            )}
          </span>
        )
      },
    },

    {
      colId: 'brand',
      field: 'brand',
      headerName: 'Бренд',
      editable: isAdmin || userRole === 'MANAGER' || userRole === 'ROP' || userRole === 'ASSISTANT',
      cellClassRules: {
        'crm-cell-invalid': (p) => {
          const v = (p.data?.brand as string | null | undefined)?.trim()
          return !v
        },
      },
      valueFormatter: (p: { value: unknown }) => {
        const v = (p.value as string | null | undefined)?.trim()
        return v ? v : '—'
      },
      valueSetter: (p: { data: ItemRow; newValue: unknown }) => {
        const raw = String(p.newValue ?? '').trim()
        p.data.brand = raw === '' || raw === '—' ? null : raw
        return true
      },
    },

    {
      // Наименование/категория из справочника номенклатуры (по умолчанию снапшот
      // парсера). Редактируется как Бренд — менеджер может поправить вручную.
      colId: 'category',
      field: 'category',
      headerName: 'Наименование',
      editable: isAdmin || userRole === 'MANAGER' || userRole === 'ROP' || userRole === 'ASSISTANT',
      valueFormatter: (p: { value: unknown }) => {
        const v = (p.value as string | null | undefined)?.trim()
        return v ? v : '—'
      },
      valueSetter: (p: { data: ItemRow; newValue: unknown }) => {
        const raw = String(p.newValue ?? '').trim()
        p.data.category = raw === '' || raw === '—' ? null : raw
        return true
      },
    },

    {
      colId: 'noteForProcurement',
      field: 'noteForProcurement',
      headerName: 'Заметка',
      wrapText: true,
      autoHeight: true,
      editable: isAdmin || userRole === 'MANAGER' || userRole === 'ROP' || userRole === 'ASSISTANT',
      cellClass: 'text-xs text-amber-700 whitespace-pre-wrap leading-snug',
      // Многострочный редактор с поддержкой Alt+Enter / Shift+Enter / Cmd+Enter
      // для перевода строки. По умолчанию AG Grid даёт agTextCellEditor (одна
      // строка) — длинный текст там читать и редактировать невозможно.
      cellEditor: TextareaEditor,
      valueFormatter: (p: { value: unknown }) => (p.value as string | null) ?? '',
      valueSetter: (p: { data: ItemRow; newValue: unknown }) => {
        const raw = String(p.newValue ?? '').trim()
        p.data.noteForProcurement = raw === '' ? null : raw
        return true
      },
    },

    {
      colId: 'qty',
      field: 'quantity',
      headerName: 'Кол-во',
      editable: isAdmin || userRole === 'MANAGER' || userRole === 'ROP' || userRole === 'ASSISTANT',
      cellClassRules: {
        'crm-cell-invalid': (p) => {
          const q = p.data?.quantity as number | null | undefined
          return q == null || q <= 0
        },
      },
      valueFormatter: (p: { data: ItemRow | undefined }) => {
        if (!p.data) return ''
        const qty = p.data.quantity as number | null
        const unit = p.data.unit as string | null
        return qty != null ? `${qty} ${unit ?? 'pcs'}` : ''
      },
      valueSetter: (p: { data: ItemRow; newValue: unknown }) => {
        const raw = String(p.newValue ?? '')
          .replace(/\s/g, '')
          .replace(',', '.')
        const num = parseFloat(raw)
        if (!Number.isFinite(num) || num <= 0) return false
        // Нормализация по unit: для шт/pcs/пустого — целое (Math.round),
        // для метрических (л/кг/гр/м) — до 3 знаков. Бэк нормализует
        // повторно как защитный слой.
        const unit = p.data.unit as string | null | undefined
        p.data.quantity = normalizeQuantityByUnit(num, unit)
        return true
      },
    },
  ]
}
